import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document } from '@contentful/rich-text-types';
import { isTypeFaq, isTypeProjectUpdates } from '@wren/shared';
import { AssetFields } from 'contentful';
import { colors, spacing } from '../../style/theme';
import RichText from '../contentful/RichText';
import { Expandable } from '../UI/Expandable';
import OrderedList from '../UI/OrderedList';
import {
  blockQuote,
  projectSubHeader,
  projectH3,
  projectText,
  simpleListStyle,
  tableStyle,
  textBlock,
} from './ProjectRenderer';
import UpdatesTimeline from './UpdatesTimeline';

export default function ProjectPageRichText({
  document,
  slug,
  ...restOfProps
}: {
  document: Document;
  slug: string;
}) {
  const renderNode: RenderNode = {
    [BLOCKS.OL_LIST]: (node, children) => {
      return <OrderedList css={projectText}>{children}</OrderedList>;
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const fields = node.data.target.fields as AssetFields;
      return (
        <div
          css={{
            margin: `${spacing.large}px 0`,
            width: '100%',
          }}
        >
          <img
            alt={fields.title}
            css={{
              maxWidth: '100%',
            }}
            src={`${fields.file?.url}`}
          />
          {fields.description && (
            <div
              css={{
                borderBottom: fields.description
                  ? `2px solid ${colors.borderColor}`
                  : '',
                color: colors.textSecondary,
                padding: spacing.small,
              }}
            >
              {fields.description}
            </div>
          )}
        </div>
      );
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const { target } = node.data;
      if (isTypeProjectUpdates<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>(target)) {
        return (
          <div css={textBlock}>
            <UpdatesTimeline slug={target.fields.slug || slug} />
          </div>
        );
      }
      if (isTypeFaq<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>(target)) {
        return (
          <Expandable
            css={{ marginBottom: spacing.default }}
            heading={target.fields.heading}
          >
            <ProjectPageRichText document={target.fields.content} slug={slug} />
          </Expandable>
        );
      }
    },
  };

  return (
    <RichText
      css={[
        textBlock,
        tableStyle,
        simpleListStyle,
        {
          '& > h2': projectSubHeader,
          '& > h3': projectH3,
          '& > p': projectText,
          '& > ul > li > p': projectText,
          '& > ol > li > p': projectText,
          '& > blockquote > p': blockQuote,
        },
      ]}
      document={document}
      renderNode={renderNode}
      withArticleStyle={false}
      {...restOfProps}
    />
  );
}
